import { createI18n } from "vue-i18n";
import DomainHandler from "./whitelabel/domainHandler";
import { Quasar } from "quasar";
import { LOCALES } from "./constants";
import defaultEnUS from "./i18n/hosts/default/en-US/index.js";

const fallbackLocale = "en-US";

// Deep merge function to properly merge nested objects
const deepMerge = (target, source) => {
  const output = Object.assign({}, target);
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      if (isObject(source[key])) {
        if (!(key in target)) Object.assign(output, { [key]: source[key] });
        else output[key] = deepMerge(target[key], source[key]);
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }
  return output;
};

const isObject = (item) => {
  return item && typeof item === "object" && !Array.isArray(item);
};

const i18n = createI18n({
  locale: LOCALES.includes(Quasar.lang.getLocale()) ? Quasar.lang.getLocale() : fallbackLocale,
  fallbackLocale: fallbackLocale,
  globalInjection: true,
  legacy: false,
  warnHtmlMessage: false,
  messages: { [fallbackLocale]: defaultEnUS },
});

const loadedLocales = new Set();

export const loadLocaleMessages = async (locale) => {
  if (loadedLocales.has(locale) || !LOCALES.includes(locale)) {
    return;
  }

  const hostName = DomainHandler.getHostname();
  const isWhiteLabel = hostName !== "default";

  let localeMessages = defaultEnUS;
  if (locale !== fallbackLocale) {
    try {
      localeMessages = (await import(`./i18n/hosts/default/${locale}/index.js`)).default;
    } catch (error) {
      console.warn(`No default messages found for locale ${locale}, using fallback locale ${fallbackLocale} messages.`);
    }
  }

  if (isWhiteLabel) {
    try {
      const whitelabelBaseMessages = (await import(`./whitelabel/base/i18n/${locale}/index.js`)).default;
      localeMessages = deepMerge(localeMessages, whitelabelBaseMessages);
    } catch (error) {
      console.warn(
        `No whitelabel base messages found for locale ${locale}, using default messages in locale ${locale}.`
      );
    }

    try {
      const whitelabelTenantMessages = (await import(`./whitelabel/tenants/${hostName}/i18n/${locale}/index.js`))
        .default;
      localeMessages = deepMerge(localeMessages, whitelabelTenantMessages);
    } catch (error) {
      console.warn(
        `No whitelabel tenant messages found for ${hostName} in locale ${locale}, using whitelabel base messages in locale ${locale}.`
      );
    }
  }

  i18n.global.setLocaleMessage(locale, localeMessages);
  loadedLocales.add(locale);
};

export const setLocale = async (locale) => {
  await loadLocaleMessages(locale);
  i18n.global.locale.value = locale;
};

export default i18n;
